import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactModal = ({ onClose }) => {
  const [formType, setFormType] = useState('demo'); // 'demo' or 'contact'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    firm_name: '',
    attorneys_count: '',
    monthly_calls: '',
    biggest_challenge: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = formType === 'demo' ? '/api/demo-request' : '/api/contact';
      
      const response = await axios.post(`${BACKEND_URL}${endpoint}`, formData);
      
      if (response.data.success) {
        setSuccess(true);
        // Reset form after 3 seconds and close
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="bg-slate-800 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="contact-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex justify-between items-center z-10">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {formType === 'demo' ? 'Book Your Demo' : 'Contact Us'}
              </h2>
              <p className="text-gray-400 text-sm">
                {formType === 'demo' 
                  ? "Let's show you how to stop losing leads"
                  : "Have questions? We're here to help"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="close-modal-button"
            >
              <FaTimes className="text-2xl" />
            </button>
          </div>

          {/* Toggle between Demo and Contact */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex space-x-4">
              <button
                onClick={() => setFormType('demo')}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                  formType === 'demo'
                    ? 'bg-emerald-500 text-slate-900'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                Book Demo
              </button>
              <button
                onClick={() => setFormType('contact')}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                  formType === 'contact'
                    ? 'bg-emerald-500 text-slate-900'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                General Inquiry
              </button>
            </div>
          </div>

          {/* Success Message */}
          {success ? (
            <div className="p-12 text-center">
              <FaCheckCircle className="text-6xl text-emerald-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">
                {formType === 'demo' ? 'Demo Request Received!' : 'Message Sent!'}
              </h3>
              <p className="text-gray-300 mb-6">
                {formType === 'demo' 
                  ? "Check your email for next steps and your Calendly link."
                  : "We'll get back to you within 24 hours."}
              </p>
              <div className="inline-block bg-emerald-500/20 border border-emerald-500/30 rounded-lg px-6 py-3">
                <p className="text-emerald-400 font-semibold">
                  Closing automatically...
                </p>
              </div>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="John Doe"
                  data-testid="input-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="john@lawfirm.com"
                  data-testid="input-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Phone Number {formType === 'demo' ? '*' : '(Optional)'}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required={formType === 'demo'}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="(555) 123-4567"
                  data-testid="input-phone"
                />
              </div>

              {/* Firm Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Law Firm Name {formType === 'demo' ? '*' : '(Optional)'}
                </label>
                <input
                  type="text"
                  name="firm_name"
                  value={formData.firm_name}
                  onChange={handleInputChange}
                  required={formType === 'demo'}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="Smith & Associates"
                  data-testid="input-firm-name"
                />
              </div>

              {/* Attorneys Count */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Number of Attorneys {formType === 'demo' ? '*' : '(Optional)'}
                </label>
                <select
                  name="attorneys_count"
                  value={formData.attorneys_count}
                  onChange={handleInputChange}
                  required={formType === 'demo'}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                  data-testid="select-attorneys-count"
                >
                  <option value="">Select...</option>
                  <option value="1-2">1-2 Attorneys</option>
                  <option value="3-5">3-5 Attorneys</option>
                  <option value="6-10">6-10 Attorneys</option>
                  <option value="11-15">11-15 Attorneys</option>
                  <option value="15+">15+ Attorneys</option>
                </select>
              </div>

              {formType === 'demo' && (
                <>
                  {/* Monthly Calls */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Approximate Monthly Inbound Calls (Optional)
                    </label>
                    <input
                      type="text"
                      name="monthly_calls"
                      value={formData.monthly_calls}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="e.g., 100-200"
                      data-testid="input-monthly-calls"
                    />
                  </div>

                  {/* Biggest Challenge */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Biggest Intake Challenge (Optional)
                    </label>
                    <textarea
                      name="biggest_challenge"
                      value={formData.biggest_challenge}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="e.g., Missing after-hours calls, slow response time..."
                      data-testid="textarea-biggest-challenge"
                    />
                  </div>
                </>
              )}

              {formType === 'contact' && (
                /* Message for Contact Form */
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="Tell us how we can help..."
                    data-testid="textarea-message"
                  />
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-slate-900 font-bold py-4 rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  formType === 'demo' ? 'Book Demo' : 'Send Message'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our terms and privacy policy.
              </p>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactModal;
